import { GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateScheduledTaskCache, useAssignResource } from 'src/api/scheduledTask'
import { invalidateTaskResourceCache } from 'src/api/task-resource'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MainButton } from 'src/components/MainButton'
import { ITaskResource } from 'src/generated-types/scheduledTask'
import { ITask } from 'src/generated-types/task'
import { Flex } from 'src/styles/flexComponent'
import { invalidateTaskCache, useAssignTaskResource, useGetTaskResources } from '../../../../api/tasks'
import GenericTable from '../../../../components/table/GenericTable'
import { TaskTypes } from '../../../../enums'
import { useDialogCtx } from '../../../../hooks/context-hooks'
import { ShowDialogProps } from '../../../../show-dialog'
import { getFormattedDate } from '../../../../utils'
import { EdgeCase, getUserDetail } from '../../../../utils/getUserDetails'
import { VesselNameCell } from '../../../localities/tasks/VesselNameCell'
import { useVesselActions } from '../../../localities/tasks/useVesselActions'
import { buildVesselRowActions } from '../../../localities/tasks/vesselRowActions'
import { buildSuccessions, buildVesselDisplayRows, IVesselDisplayRow } from '../../../localities/tasks/vesselSuccession'
import AssignResourceForm from './AssignResourceForm'

interface TaskResourcesProps extends ShowDialogProps {
  task: ITask
  customerId: EntityId
  localityId: EntityId
  taskId: EntityId
  scheduleTaskId?: EntityId
  scheduleTaskStartDate?: Date
  scheduleTaskEndDate?: Date
}
const TaskResources: FC<TaskResourcesProps> = ({
  customerId,
  localityId,
  taskId,
  scheduleTaskId,
  scheduleTaskStartDate,
  scheduleTaskEndDate,
  task,
  pop,
}) => {
  const { t: translate } = useTranslation(['task', 'common', 'common-api-message'])
  const { showDialog } = useDialogCtx()
  const { mutateAsync } = useAssignTaskResource(localityId, taskId)
  const { mutateAsync: mutateScheduledWorkOrderResourceAssign } = useAssignResource(
    localityId,
    scheduleTaskId as EntityId,
  )
  const { data: taskResources } = useGetTaskResources(localityId, taskId, scheduleTaskId)
  const [expandedRoots, setExpandedRoots] = useState<Record<number, boolean>>({})

  const refreshVessels = useCallback(() => {
    invalidateTaskCache.getTaskResources(localityId, taskId)
    invalidateScheduledTaskCache.invalidateAllScheduledTasks(localityId)
    invalidateTaskResourceCache.getRelatedTasks(customerId)
  }, [customerId, localityId, taskId])

  const { onEdit, onRevert, onDelete } = useVesselActions({
    localityId,
    taskId,
    workOrderStart: scheduleTaskStartDate,
    workOrderEnd: scheduleTaskEndDate,
    onChanged: refreshVessels,
  })

  const successions = useMemo(
    () => buildSuccessions((taskResources ?? []) as unknown as ITaskResource[]),
    [taskResources],
  )

  const canAct = useMemo(() => {
    const occurrence = successions[0]?.root?.scheduledTask
    return !occurrence?.signedDate && !occurrence?.isIgnored
  }, [successions])

  const toggleRoot = useCallback((rootId: number) => {
    setExpandedRoots(previous => ({ ...previous, [rootId]: !previous[rootId] }))
  }, [])

  const rows: IVesselDisplayRow[] = useMemo(
    () =>
      buildVesselDisplayRows({
        successions,
        expandedRoots,
        canAct: canAct && !!scheduleTaskId,
        workOrderStart: scheduleTaskStartDate,
        workOrderEnd: scheduleTaskEndDate,
      }),
    [successions, expandedRoots, canAct, scheduleTaskId, scheduleTaskStartDate, scheduleTaskEndDate],
  )

  const assignVessel = useCallback(
    (replaceTarget: ITaskResource | null) => {
      const parameters = {}
      if (task.type == TaskTypes.Repeatable) {
        parameters['startDate'] = task?.startDate
        parameters['endDate'] = task?.endDate
      }
      showDialog(AssignResourceForm, {
        title: translate(replaceTarget ? 'replace-vessel' : 'assign-resource', { ns: 'task' }),
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          customerId: customerId,
          task: { ...task, ...parameters },
          scheduleTask: scheduleTaskId ? { startDate: scheduleTaskStartDate, endDate: scheduleTaskEndDate } : undefined,
          allowDateEntry: !!scheduleTaskId && !!replaceTarget,
          replaceTarget: replaceTarget,
          onSubmit: async (formValues, customerId) => {
            formValues.tasksId = task.id
            formValues.scheduledTaskId = scheduleTaskId
            if (replaceTarget) {
              formValues.replacedTaskResourceId = replaceTarget.id
            }
            const onError = error => {
              if (error['status'] == 409) {
                toast.error(translate('conflict-date', { ns: 'task' }))
              } else {
                toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
              }
            }
            if (scheduleTaskId) {
              await mutateScheduledWorkOrderResourceAssign(
                { ...formValues, customerId: customerId as EntityId },
                { onError, onSuccess: refreshVessels },
              )
            } else {
              await mutateAsync(
                { ...formValues, customerId: customerId as EntityId },
                { onError, onSuccess: refreshVessels },
              )
            }
          },
        },
      })
    },
    [
      customerId,
      mutateAsync,
      mutateScheduledWorkOrderResourceAssign,
      refreshVessels,
      scheduleTaskEndDate,
      scheduleTaskId,
      scheduleTaskStartDate,
      showDialog,
      task,
      translate,
    ],
  )

  const onAssign = useCallback(() => assignVessel(null), [assignVessel])

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'resource.name',
        headerName: translate('name', { ns: 'common' }),
        flex: 1.4,
        cellClassName: 'font-weight-semi-bold',
        renderCell: ({ row }) => <VesselNameCell row={row as IVesselDisplayRow} onToggle={toggleRoot} />,
      },
      {
        field: 'resource.description',
        headerName: translate('description', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => row.description,
      },
      {
        field: 'startDate',
        headerName: translate('startDate', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => (row.startDate ? getFormattedDate(row.startDate) : '-'),
      },
      {
        field: 'endDate',
        headerName: translate('endDate', { ns: 'common' }),
        flex: 1,
        renderCell: ({ row }) => (row.endDate ? getFormattedDate(row.endDate) : '-'),
      },
      {
        field: 'user',
        flex: 1,
        headerName: translate('responsibleUser', { ns: 'common' }),
        renderCell: ({ row }) => {
          const user = (row as IVesselDisplayRow).taskResource?.resource?.user
          return (
            <DynamicTextTypography
              text={user ? getUserDetail(user, undefined, EdgeCase.ReturnEmailForNewUser) : '-'}
              maxLength={15}
            />
          )
        },
      },
      {
        field: 'actions',
        headerName: '',
        type: 'actions' as GridNativeColTypes,
        width: 60,
        getActions: ({ row }) =>
          buildVesselRowActions({
            row: row as IVesselDisplayRow,
            translate,
            onReplace: row => assignVessel(row.taskResource),
            onEdit,
            onRevert,
            onDelete,
          }),
      },
    ],
    [translate, toggleRoot, assignVessel, onEdit, onRevert, onDelete],
  )

  return (
    <GenericDialogLayout
      title={translate('resources')}
      pop={pop}
      topActions={
        task.type === TaskTypes.Once || scheduleTaskId
          ? [
              <Flex.Row key="resources-action" px={3}>
                <MainButton onClick={onAssign}>{translate('assign', { ns: 'common' })}</MainButton>
              </Flex.Row>,
            ]
          : []
      }
    >
      <GenericTable
        data={rows}
        columns={columns}
        id={'customer-task-resources'}
        autoHeight={false}
        height={'300px'}
        getRowHeight={({ model }) => ((model as IVesselDisplayRow)?.chain ? 60 : 44)}
      />
    </GenericDialogLayout>
  )
}

export default TaskResources
